import React, { useMemo, useState, type SyntheticEvent } from 'react';
import { useFormik } from 'formik';
import { TipsAndUpdatesOutlined } from '@mui/icons-material';
import { FormControl, RadioGroup, Tooltip } from '@mui/material';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { array, number, object, string } from 'yup';
import {
  FundingPanel,
  FundingSourceBox,
  getTagsByFundingSource,
  panelOptions,
  type FundingPlan,
  FundingSource,
  getFundingBasedDeclaration,
  fundingDeclarationMappings,
  needMakeupStatus,
} from '.';
import type {
  FundingsValue,
  FundingPreSelectValue,
  FundingMappings,
  ConfigurationMapping,
} from './types';
import {
  FUNDING_TAGS,
  fundingTypeText,
  fundingValidTags,
  MappingType,
} from '../../constant';
import { PAGE_TEXT } from '../../lang';
import {
  useUpdateNomination,
  useUpdateSubjectClassification,
} from '../../../graphql';
import { ProgrammeTypes } from '../../../constants';
import { handleReadyNominationSubmit } from '../../../../../utils';
import {
  Accordion,
  Alert,
  type AlertDetails,
  Button,
  Checkbox,
  Dialog,
  Radio,
  Select,
  type SelectOption,
  TextField,
} from '../../../../../components';
import { FUNDING_STATUS_DISPLAY_STRING } from '../../../../ScholarshipManagement/constants/fundingStatus';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

type FundingsProps = {
  applicationNo: string;
  applicationId: string;
  subjectClassification: string;
  competitiveStudentship: number | null;
  isViewMode?: boolean;
  fundings: FundingsValue[];
  sources: FundingSource[];
  programmeInGsb: string;
  departmentOptions: SelectOption[];
  fundingMapping: FundingMappings;
  refetchFunding: () => void;
};

const FUNDING_TEXT = PAGE_TEXT.FUNDING;

const handleDisplayHint = (hint: string): React.ReactNode => {
  return <h2 className="my-2 pl-8 italic font-bold text-[#951D1D]">{hint}</h2>;
};

const handleReturnFundingOption = (
  fundingMapping: FundingMappings,
): SelectOption[] => {
  return Object.entries(fundingMapping).map((item: any) => ({
    label: fundingTypeText[item[0] as MappingType],
    value: item[0],
  }));
};

export const Fundings: React.FC<FundingsProps> = props => {
  const { nominationId } = useParams();

  const {
    fundingMapping,
    fundings: initialFundings,
    sources: initialSources,
    programmeInGsb,
    departmentOptions,
    applicationNo,
    applicationId,
    subjectClassification: initialSubjectClassification,
    competitiveStudentship: initialCompetitiveStudentship,
    refetchFunding,
    isViewMode = false,
  } = props;

  const getInitialSelectedFunding = (): FundingsValue | undefined => {
    const flatSources = Object.entries(fundingMapping).flatMap(
      (item: any) => item[1],
    );

    return initialFundings.find(({ source }) => {
      return flatSources.find(({ value }) => {
        return value === source;
      });
    });
  };

  const initialSelectedFunding = getInitialSelectedFunding();

  const competitiveValueOrderedList = fundingMapping.COMPETITIVE.map(
    item => item.value,
  );

  const fundingTypeOptions: SelectOption[] =
    handleReturnFundingOption(fundingMapping);

  const [openDialog, setOpenDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const fundingsFormik = useFormik<FundingPreSelectValue>({
    initialValues: {
      schema: initialSelectedFunding?.schema ?? '',
      sources: initialSources,
      subjectClassification: initialSubjectClassification ?? '',
      competitiveStudentship: initialCompetitiveStudentship ?? '',
    },
    validationSchema: object({
      schema: string().required('Required'),
      sources: array().min(1, 'Required'),
      subjectClassification: string().when(['schema'], {
        is: (schema: string) => schema === MappingType.COMPETITIVE,
        then: schema =>
          schema
            .required()
            .oneOf([
              FundingPanel.HUMANITIES_SOCIAL_SCIENCES_BUSINESS_STUDIES,
              FundingPanel.SCIENCES_MEDICINE_ENGINEERING_TECHNOLOGY,
            ]),
        otherwise: schema => schema.notRequired(),
      }),
      competitiveStudentship: number().when(['schema'], {
        is: (schema: string) => schema === MappingType.COMPETITIVE,
        then: schema =>
          schema.required('Required').test({
            message: 'Out of Range',
            test: value => value >= 0,
          }),
        otherwise: schema => schema.notRequired(),
      }),
    }),
    onSubmit: value => {
      let invalidReason: string = '';
      const existDisableFunding = value.sources.some(source => {
        // can not submit if theres disabled funding

        const fundingConfig = fundingMapping[value.schema as MappingType].find(
          (funding: ConfigurationMapping) => funding.value === source,
        );
        invalidReason = fundingConfig?.disabledDesc ?? '';
        return fundingConfig?.disabled ?? false;
      });
      if (existDisableFunding && invalidReason) {
        setAlertDetails({
          alertContent: invalidReason,
          alertType: 'warning',
        });
        setOpenAlert(true);
        return;
      }

      setOpenDialog(true);
    },
  });

  const handleChangeCompetitive =
    (value: string) =>
    async (
      event: SyntheticEvent<Element, Event>,
      checked: boolean,
    ): Promise<void> => {
      const { sources = [] } = fundingsFormik.values;
      //  should keep the order
      const newSources = checked
        ? [...sources, value]
        : sources.filter(condition => condition !== value);

      const sortedSources = (newSources as FundingSource[]).sort(
        (a, b) =>
          competitiveValueOrderedList.indexOf(a) -
          competitiveValueOrderedList.indexOf(b),
      );

      await fundingsFormik.setFieldValue('sources', sortedSources);
    };

  const handleChangeBesidesCompetitive = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await fundingsFormik.setFieldValue('sources', [event.target.value]);
  };

  const handleGetSourceLabel = (
    sourceVal: FundingSource,
  ): string | undefined => {
    const flatSources = Object.entries(fundingMapping).flatMap(
      (item: any) => item[1],
    );
    const source = flatSources?.find(option => option.value === sourceVal);
    return source?.label;
  };

  const handleConfirmChange = async (): Promise<void> => {
    await handleInitialize({
      schema: fundingsFormik.values.schema,
      sources: fundingsFormik.values.sources,
      subjectClassification: fundingsFormik.values.subjectClassification,
      competitiveStudentship: fundingsFormik.values.competitiveStudentship,
    });
    setOpenDialog(false);
  };

  const handleCancelChange = (): void => {
    setOpenDialog(false);
  };
  const sourcesList =
    fundingMapping[fundingsFormik.values.schema as MappingType] ?? [];
  const showSchemaList: boolean =
    fundingsFormik.values.schema !== '' && sourcesList.length !== 0;
  const enableMultiselect =
    fundingsFormik.values.schema === MappingType.COMPETITIVE;

  const handleSchemeChange = async (
    event: React.ChangeEvent<{ value: string }>,
  ): Promise<void> => {
    const newScheme = event.target.value as MappingType;
    await fundingsFormik.setFieldValue('schema', newScheme);
    await fundingsFormik.setFieldValue('sources', []);
    await fundingsFormik.setFieldValue('subjectClassification', '');
    await fundingsFormik.setFieldValue('competitiveStudentship', '');
    await fundingsFormik.setTouched({ sources: false });
    document.getElementById('sources')?.focus();
  };

  const handleChangePanelSelection = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const { value } = event.target;
    await fundingsFormik.setFieldValue('subjectClassification', value);
  };

  const isFourYearPhd =
    programmeInGsb === ProgrammeTypes.FOUR_YEAR_FULL_TIME_PHD;
  const updateNomination = useUpdateNomination(refetchFunding);
  const updateSubjectClassification = useUpdateSubjectClassification();
  const handleSubmit = async (
    values: FundingsValue,
    configTags: FUNDING_TAGS[],
  ): Promise<void> => {
    const {
      fundingPlan,
      projectInformation: projectInfo,
      proofOfScheme = [],
      declaration,
      supportYears,
      status,
      ...filteredValues
    } = values;

    const isFourYearAllocation =
      values.source ===
        FundingSource.ALLOCAION_PHD_FOR_MAJOR_RENOWNED_FUNDING_AND_RIO &&
      isFourYearPhd;

    const isPolyuCompetitive =
      values.source === FundingSource.POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME;

    const isUniversityDual =
      values.source === FundingSource.DUAL_PHD_DEGREE_PROGRAMME;

    const specialPanelCase = isPolyuCompetitive || isUniversityDual;

    const makeupAccountCodeMode =
      specialPanelCase && needMakeupStatus.some(sta => status === sta);
    const needOptionalAccountCode =
      values.source === FundingSource.SELF_FINANCED;

    const shouldKeepFundingPlan =
      isFourYearAllocation ||
      (configTags.includes(FUNDING_TAGS.NEED_ACCOUNT) &&
        (!isPolyuCompetitive || makeupAccountCodeMode)) ||
      needOptionalAccountCode;

    const shouldKeepProjectInfo = configTags.includes(
      FUNDING_TAGS.NEED_PROJECT_ROLE,
    );

    const shouldKeepDeclaration = values.source in fundingDeclarationMappings;

    const processedFundingPlan: FundingPlan[] = fundingPlan.map(plan => ({
      accountCode: plan.accountCode,
      period: {
        from: dayjs(plan.period.from)
          .set('date', 1)
          .set('hour', 0)
          .set('minute', 0)
          .set('second', 0)
          .set('millisecond', 0)
          .tz('Asia/Hong_Kong', true)
          .toDate(),
        to: dayjs(plan.period.to)
          .endOf('month')
          .set('hour', 23)
          .set('minute', 59)
          .set('second', 59)
          .set('millisecond', 0)
          .tz('Asia/Hong_Kong', true)
          .toDate(),
      },
    }));
    const needProofDocs = [
      FUNDING_TAGS.NEED_PROOF_GS_EMAIL,
      FUNDING_TAGS.NEED_PROOF_RIO_EMAIL,
      FUNDING_TAGS.NEED_PROOF_RESEARCH_UNIT_EMAIL,
      FUNDING_TAGS.NEED_PROOF_PROJECT_RECORD,
    ].some(tag => configTags.includes(tag));

    const batchInput: Array<Partial<FundingsValue>> = initialFundings.map(
      (funding: FundingsValue) => {
        // if the funding exists, update the funding
        if (funding.source !== values.source) {
          const { supportYears, status, ...fundingVal } = funding;
          return fundingVal;
        }

        return {
          ...(shouldKeepFundingPlan
            ? { fundingPlan: processedFundingPlan }
            : {}),
          ...(shouldKeepDeclaration ? { declaration } : {}),
          ...(shouldKeepProjectInfo ? { projectInformation: projectInfo } : {}),
          ...(needProofDocs ? { proofOfScheme } : {}),
          ...filteredValues,
          formFinished: true,
        };
      },
    );

    const input = {
      fundings: batchInput,
    };

    const variables = {
      id: nominationId,
      input,
    };

    try {
      await updateNomination({
        variables,
      });
      refetchFunding();
      setAlertDetails({
        alertContent: 'Saved Funding Source Successfully.',
        alertType: 'success',
      });
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Saved Funding Source Failed. ${
          (error as Error)?.message
        }`,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const handleInitialize = async (
    fundingPreSelectValue: FundingPreSelectValue,
  ): Promise<void> => {
    const {
      schema: newSchema,
      sources: newSources,
      subjectClassification,
      competitiveStudentship,
    } = fundingPreSelectValue;

    const batchInput: Array<Partial<FundingsValue>> = newSources.map(
      (source: FundingSource) => {
        // if the item is in the original fundings, copy the config
        const found = initialFundings.find(item => source === item.source);
        if (found) {
          const { status, supportYears, ...foundOthers } = found;
          return foundOthers;
        }

        const tags = getTagsByFundingSource(fundingMapping, newSchema, source);

        const needDeclaration = source in fundingDeclarationMappings;

        // check if should provide a default account code
        const needDefaultAccount = tags.includes(
          FUNDING_TAGS.NEED_DEFAULT_ACCOUNT_FOR_FIRST_YEAR,
        );

        // if no more information required, should pass *formFinished:true* to backend.
        const nothingRequired =
          !tags.some(tag => fundingValidTags.includes(tag)) && !needDeclaration;

        const needInitialDeclaration = source in fundingDeclarationMappings;

        return {
          schema: newSchema,
          source,
          sufficientFundingConfirm: false,
          ...(needInitialDeclaration
            ? { declaration: [getFundingBasedDeclaration(source)] }
            : {}),
          ...(needDefaultAccount
            ? {
                fundingPlan: [
                  {
                    accountCode: 'DEFAULT',
                    period: { from: '', to: '' },
                  },
                ],
              }
            : {}),
          formFinished: nothingRequired,
        };
      },
    );

    // if both of previous schema and current schema are competitive, then make a merge
    // if current schema is non competitive or other, then just pass an array that length equals 1 to overwrite
    const input = {
      fundings: batchInput,
    };

    const variables = {
      id: nominationId,
      input,
    };

    try {
      if (
        newSchema === MappingType.COMPETITIVE &&
        Boolean(subjectClassification) &&
        competitiveStudentship !== undefined
      ) {
        // should update subjectClassification (panel type) when the schema is competitive
        await updateSubjectClassification({
          variables: {
            applicationId,
            subjectClassification,
            competitiveStudentship,
          },
        });
      }

      await updateNomination({
        variables,
      });
      refetchFunding();
      setAlertDetails({
        alertContent:
          'Updated Funding Source Successfully. ' + FUNDING_TEXT.UPDATE_SUCCESS,
        alertType: 'success',
      });
    } catch (error: any) {
      setAlertDetails({
        alertContent: `Updated Funding Source Failed.${
          (error as Error)?.message
        }`,
        alertType: 'error',
      });
    } finally {
      setOpenAlert(true);
    }
  };

  const handleDisplayFundingForm = useMemo((): React.ReactNode => {
    return (
      <div className="!bg-[#F6F6F6]">
        {initialSources?.map((source: FundingSource, index) => {
          const sourceLabel = handleGetSourceLabel(source);

          const savedFormValue = initialFundings.find(
            (formVal: FundingsValue) => formVal.source === source,
          );
          let fundingValue: FundingsValue = {
            schema: initialSelectedFunding?.schema ?? '',
            sufficientFundingConfirm: false,
            stipendBudgetCreationConfirm: false,
            externalGrantsConfirm: false,
            accountInfoAccurateConfirm: false,
            source,
            fundingPlan: [
              {
                accountCode: '',
                period: { from: '', to: '' },
              },
            ],
            requiredDocuments: [],
            projectInformation: {
              role: '',
              PIName: '',
              PIDepartment: null,
              justification: '',
            },
            proofOfScheme: [],
            declaration: [],
          };

          if (savedFormValue) {
            const { projectInformation, fundingPlan, ...others } =
              savedFormValue;

            const needOptionalAccountCode =
              savedFormValue.source === FundingSource.SELF_FINANCED; // in this case, show [] account code initially
            fundingValue = {
              fundingPlan:
                savedFormValue?.fundingPlan?.length === 0 &&
                !needOptionalAccountCode
                  ? [
                      {
                        accountCode: '',
                        period: { from: '', to: '' },
                      },
                    ]
                  : fundingPlan,
              projectInformation:
                projectInformation?.PIName === undefined
                  ? {
                      role: '',
                      PIName: '',
                      PIDepartment: null,
                      justification: '',
                    }
                  : projectInformation,
              ...others,
            };
          }

          const isFourYearAllocation =
            source ===
              FundingSource.ALLOCAION_PHD_FOR_MAJOR_RENOWNED_FUNDING_AND_RIO &&
            isFourYearPhd;

          const isPolyuCompetitive =
            source === FundingSource.POLYU_COMPETITIVE_PHD_FELLOWSHIP_SCHEME;

          const fundingStatus =
            FUNDING_STATUS_DISPLAY_STRING[
              savedFormValue?.status as keyof typeof FUNDING_STATUS_DISPLAY_STRING
            ] ?? '';

          return (
            sourceLabel !== undefined && (
              <div key={index}>
                <div className="!px-4 z-40 bg-white">
                  <FundingSourceBox
                    isViewMode={isViewMode}
                    title={`${index + 1}. ${sourceLabel} ${savedFormValue?.status != null ? ` - ${fundingStatus}` : ''}`}
                    initialFundingValues={fundingValue}
                    applicationNo={applicationNo}
                    programmeInGsb={programmeInGsb as ProgrammeTypes}
                    isFourYearAllocation={isFourYearAllocation}
                    isPolyuCompetitive={isPolyuCompetitive}
                    departmentOptions={departmentOptions}
                    fundingMapping={fundingMapping}
                    handleSubmit={handleSubmit}
                  />
                </div>
              </div>
            )
          );
        })}
      </div>
    );
  }, [initialFundings, initialSources]);

  return (
    <div className="mt-[1rem]">
      <Accordion
        className="w-full pb-2"
        title={FUNDING_TEXT.TITLE}
        defaultExpanded
        disableExpand
        details={
          <div className="w-full">
            <form
              onSubmit={handleReadyNominationSubmit(fundingsFormik)}
              noValidate
            >
              <div className="w-full flex flex-row px-6 pt-4 pb-2 gap-2">
                <TipsAndUpdatesOutlined color="primary" />
                <div
                  className="text-[#951D1D]"
                  dangerouslySetInnerHTML={{
                    __html: FUNDING_TEXT.NOTICE,
                  }}
                />
              </div>

              <div className="w-full flex flex-row gap-4 p-8">
                <h2 className="font-bold mt-[0.9rem]">
                  {FUNDING_TEXT.FUNDING_SELECTION}
                </h2>
                <Select
                  id="schema"
                  name="schema"
                  className={'w-1/4'}
                  disabled={isViewMode}
                  options={fundingTypeOptions}
                  InputLabelProps={{ shrink: true }}
                  value={fundingsFormik.values.schema}
                  onBlur={fundingsFormik.handleBlur}
                  onChange={handleSchemeChange}
                  error={
                    Boolean(fundingsFormik.touched.schema) &&
                    Boolean(fundingsFormik.errors.schema)
                  }
                  helperText={
                    fundingsFormik.touched.schema &&
                    fundingsFormik.errors.schema
                  }
                />
              </div>
              {showSchemaList && (
                <div className="w-full py-2 bg-[#F8F8F8]" id="sources">
                  {enableMultiselect ? (
                    <>
                      <div className="flex flex-row items-center">
                        {handleDisplayHint(FUNDING_TEXT.MUITI_OPTIONS)}
                        <h2 className="pl-2 !text-[#d32f2f] text-sm italic">
                          {fundingsFormik.touched.sources && (
                            <>{fundingsFormik.errors.sources}</>
                          )}
                        </h2>
                      </div>
                      <div>
                        {sourcesList?.map((source, index) => {
                          const {
                            value,
                            label,
                            disabled: apiDisabled = false,
                            disabledDesc = '',
                            recentOpeningPeriod,
                          } = source;

                          const ticked = fundingsFormik.values.sources.includes(
                            value as FundingSource,
                          );
                          // show ddl if any
                          let closedText: string = '';
                          if (recentOpeningPeriod?.to !== undefined)
                            closedText = ` Deadline: ${dayjs(recentOpeningPeriod?.to).format('DD/MM/YYYY')}`;
                          return (
                            <div
                              key={index}
                              className={`${index % 2 === 0 ? 'bg-[#E4E4E4]' : ''} w-full py-1`}
                            >
                              <Checkbox
                                size="small"
                                name="sources"
                                className="pl-14"
                                label={
                                  <Tooltip title={disabledDesc}>
                                    <span
                                      className={`${apiDisabled && 'text-[#8D8D8D]'}`}
                                    >
                                      {label}&nbsp;
                                      <small className="text-[#951D1D] ml-4">
                                        {closedText}
                                      </small>
                                    </span>
                                  </Tooltip>
                                }
                                value={ticked}
                                /**
                                 * disabled when
                                 * 1. monitor, every things should be in view mode
                                 * 2. api pass disabled === true & current value===true (just in case user can un-tick)
                                 */
                                disabled={
                                  isViewMode || (apiDisabled && !ticked)
                                }
                                onChange={handleChangeCompetitive(value)}
                                error={
                                  !apiDisabled &&
                                  Boolean(fundingsFormik.touched.sources) &&
                                  Boolean(fundingsFormik.errors.sources)
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-row items-center">
                        {handleDisplayHint(FUNDING_TEXT.ONE_OPTIONS)}
                        <h2 className="pl-2 !text-[#d32f2f] text-sm italic">
                          {fundingsFormik.touched.sources && (
                            <>{fundingsFormik.errors.sources}</>
                          )}
                        </h2>
                      </div>
                      <FormControl className="w-full">
                        <RadioGroup
                          name="sources"
                          value={fundingsFormik.values.sources?.[0] ?? ''} // RadioGroup does not accept undefined as value
                          onChange={handleChangeBesidesCompetitive}
                        >
                          {sourcesList?.map((source, index) => {
                            const {
                              value,
                              label,
                              disabled = false,
                              disabledDesc = '',
                              recentOpeningPeriod,
                            } = source;

                            const checked =
                              fundingsFormik.values.sources.includes(
                                value as FundingSource,
                              );

                            // show ddl if any
                            let closedText: string = '';
                            if (recentOpeningPeriod?.to !== undefined)
                              closedText = ` Deadline: ${dayjs(recentOpeningPeriod?.to).format('DD/MM/YYYY')}`;

                            return (
                              <div
                                key={index}
                                className={`${index % 2 === 0 ? 'bg-[#E4E4E4]' : ''} w-full py-1`}
                              >
                                <Radio
                                  size="small"
                                  className="pl-14"
                                  label={
                                    <Tooltip title={disabledDesc}>
                                      <span>
                                        {label}
                                        <small>{closedText}</small>
                                      </span>
                                    </Tooltip>
                                  }
                                  value={value}
                                  disabled={disabled || isViewMode}
                                  checked={checked}
                                  error={
                                    !disabled &&
                                    fundingsFormik.touched.sources &&
                                    Boolean(fundingsFormik.errors.sources)
                                  }
                                />
                              </div>
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    </>
                  )}
                </div>
              )}

              {showSchemaList && enableMultiselect && (
                <div className="px-8">
                  <h2 className="font-bold my-[0.9rem]">
                    {FUNDING_TEXT.DISCIPLINES}
                  </h2>

                  <FormControl className="w-full">
                    <RadioGroup
                      name="subjectClassification"
                      id="subjectClassification"
                      value={fundingsFormik.values.subjectClassification ?? ''} // RadioGroup does not accept undefined as value
                      onChange={handleChangePanelSelection}
                    >
                      {panelOptions?.map((panel, index) => {
                        const { value, label } = panel;
                        const checked: boolean =
                          fundingsFormik.values.subjectClassification === value;

                        return (
                          <div key={index} className="w-full py-1 pl-8">
                            <Radio
                              size="small"
                              label={label}
                              value={value}
                              checked={checked}
                              disabled={isViewMode}
                              error={
                                Boolean(
                                  fundingsFormik.touched.subjectClassification,
                                ) &&
                                Boolean(
                                  fundingsFormik.errors.subjectClassification,
                                )
                              }
                            />
                          </div>
                        );
                      })}
                    </RadioGroup>
                  </FormControl>

                  <h2 className="mt-[2rem] mb-2">
                    {FUNDING_TEXT.UPSF_INDICATION}
                  </h2>
                  <h2
                    className="mb-4"
                    dangerouslySetInnerHTML={{
                      __html: FUNDING_TEXT.STUDENTSHIPS_FUNDED_BY_UPSF,
                    }}
                  />
                  <div className="pl-8">
                    <TextField
                      label=""
                      name="competitiveStudentship"
                      type="number"
                      required
                      disabled={isViewMode}
                      onChange={fundingsFormik.handleChange}
                      onBlur={fundingsFormik.handleBlur}
                      value={fundingsFormik.values.competitiveStudentship}
                      error={
                        fundingsFormik.touched.competitiveStudentship &&
                        Boolean(fundingsFormik.errors.competitiveStudentship)
                      }
                      helperText={
                        fundingsFormik.touched.competitiveStudentship &&
                        fundingsFormik.errors.competitiveStudentship
                      }
                    />
                  </div>
                </div>
              )}

              <div className="p-8 my-4">
                {!isViewMode && (
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!sourcesList.length} // disable the confirm button if no available funding
                  >
                    {FUNDING_TEXT.CONFIRM}
                  </Button>
                )}
              </div>
            </form>
            {handleDisplayFundingForm}
          </div>
        }
      />
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={openDialog}
        dialogTitle={FUNDING_TEXT.CHANGE_FUNDING_DIALOG.TITLE}
        dialogAction={PAGE_TEXT.YES}
        cancelAction={PAGE_TEXT.CANCEL}
        maxWidth={'sm'}
        onDialogAction={handleConfirmChange}
        onDialogClose={handleCancelChange}
      />
    </div>
  );
};
